// @ts-strict-ignore
/**
 * This file contains Redux thunks that serve two main purposes:
 *
 * 1. Coordinate and dispatch a series of actions to accomplish a single task.
 *    Some Redux "transactions" require a specific order of dispatches, so
 *    these orchestration thunks wrap them in-series, simplifying consumer code
 *    and providing a safe API that guarantees all actions will be dispatched (unless one fails).
 *
 * 2. Dispatch actions that require part of the state in their payloads.
 *    Some reducers need to use the existing state as part of their responsibility (eg. to copy
 *    one field from one part of the state to another). Reducers need to be pure functions, and
 *    actions should contain all the information needed to complete a task. These
 *    thunks receive the least amount of arguments needed to read from the state and dispatch
 *    actions that contain the fully prepared payload. This simplifies consumer code by eliminating
 *    the need to read from the state and dispatch an action in all components that need it.
 */

import type { WorkflowNode } from 'flow/service/graph';
import {
  findNextActionBasedOnPrecedence,
  DEFAULT_OUTCOME_PRECEDENCE_INDEX,
  isActionOfType,
  validatePrecedenceIndexedAction,
  isActionPayloadEphemeral,
  getActionName,
  generateTemporaryMatrixModelToken,
} from 'flow/service/actions';
import generateVersionChangelog, { formatters } from 'flow/service/changelog';
import { DEFAULT_WORKFLOW_ROUTING_PRECEDENCE_INDEX } from 'flow/service/actions/workflowRouting';
import type { AppDispatch, State } from '~/store';
import {
  createActions,
  createThreshold,
  removeAction,
  removeThreshold,
  resetLinkedChildActionState,
  resetNodeAndConnectorState,
  setConnectorInteractionState,
  setFocusedNodeId,
  setLinkedChildActionOwnerNodeId,
  updateLinkedChildActionThresholdIds,
  updateAction,
  updateThreshold,
  setSearchMatchIndex,
  setIsSearchBoxVisible,
  setSearchQuery,
  setNodeFolderCollapsed,
  setShouldRenderCriticalNodePath,
  removeEphemeralProp,
  addEphemeralTagToProps,
  addEphemeralOutcomeToProps,
  addAction,
  updateDependencies,
  setSnippetCollapsed,
} from '../slice';
import {
  selectDefaultOutcomeAction,
  selectLinkedChildActionState,
  selectPreviewAction,
  selectPreviewPrecedenceIndexedAction,
  selectWorkflowProps,
  selectInitialWorkflowProps,
  selectInitialWorkflowData,
  selectWorkflowData,
  selectDefaultWorkflowRoutingAction,
  selectOutcomeActions,
  selectPrecedenceIndexedActions,
  selectSearchMatchIndex,
  selectNodesMatchingSearch,
  selectNodeFolderState,
  selectCollapsedInputHasMatches,
  selectCollapsedSnippetIdsWithMatches,
  selectFocusedAction,
  selectFeatureFlagsForStore,
  selectShouldRenderCriticalNodePath,
  selectActionByPublicId,
  selectThresholdsByIds,
  selectDataRoutesByTargetId,
  selectSnippetCollapsedState,
} from '../selectors';
import { ActionType } from '../../engine/constants';
import { generatePublicId } from 'flow/service/publicIds';
import { elementTypes } from '../../engine/types';
import type { Action, OutcomeObjectInAction, PublicId } from '../../engine/types';
import type { CreateActionPayload } from '../../engine/create/actions/types';
import { extractThresholdIds } from '../../../service/thresholds';

// importing slice by its namespace since it doesn't belong to the wf editor
import * as testingSuiteActions from '~/flow/TestingSuite/redux/slice';
import { createEphemeralOutcome, getOutcomeByName, isOutcomeEphemeral } from '../../../service/outcomes/outcomes';
import { getTagByName, createEphemeralTag, isTagEphemeral } from 'flow/service/tags/tags';
import { getFormattedTagData } from 'flow/store/engine/transformation/actions';
import { duplicateElements, extractIds } from 'flow/service/general';
import { generateRandomizedIdPublicIdMap, remapIds } from 'flow/service/publicIds/regenerateIds';
import { discardWorkflowHistoryTransaction } from './history';

/**
 * Initializes the linked child action flow by creating a threshold, a preview action,
 * and connecting them.
 */
export function beginLinkedChildActionThunk(parentNodeId: WorkflowNode['id']) {
  /**
   * In order to initialize the linked child action flow, we create a threshold and preview action
   * and that is focused so that the user can edit its thresholds
   */
  return (dispatch: AppDispatch) => {
    const thresholdId = generatePublicId('threshold');
    const previewActionPublicId = generatePublicId('action', ActionType.PREVIEW);


    dispatch(updateLinkedChildActionThresholdIds({ operation: 'add', thresholdId }));
    dispatch(setLinkedChildActionOwnerNodeId(parentNodeId));
    dispatch(setConnectorInteractionState('focused'));
    dispatch(createActions({
      type: ActionType.PREVIEW,
      args: {
        publicId: previewActionPublicId,
      },
    }));
    dispatch(setFocusedNodeId(previewActionPublicId));
    dispatch(createThreshold({
      isPreview: false,
      path: `${thresholdId}.left.actionId`,
      value: {
        actionId: previewActionPublicId,
        leftActionId: parentNodeId,
      },
    }));
  };
}


/**
 * Saves the changes from linked child action into the workflow policy
 */
export function saveLinkedChildActionThunk() {
  /**
   * When saving a linked child action, we replace the existing preview action with a new
   * action of the type specified, inheriting the threshold of the preview one. Prior to removing
   * the preview action, we map and copy both threshold and rule data to the new action, and
   * subsequently delete them from the preview action before deleting the preview action itself.
   * Deleting the thresholds and rules from the preview action before deleting the action itself
   * is necessary, as deleting an action triggers the automatic removal of all its dependencies,
   * including the threshold.
   */
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const {
      createActionPayload,
      thresholdIds,
      ownerNodeId,
    } = selectLinkedChildActionState(state);
    const previewActionBeingBuilt = selectPreviewAction(state);
    const outcomeActions = selectOutcomeActions(state);

    /**
     * it's possible that the user wants to link a node to an existing outcome from linked child action.
     * we run the check to assess wether or not that's the case, and run slightly different logic
     * for this scenario.
     */
    if (createActionPayload.type === ActionType.OUTCOME) {
      const outcomeActionBeingLinked = outcomeActions
        .find(outcomeAction => outcomeAction.outcome.name === createActionPayload.args.name);

      if (outcomeActionBeingLinked) {
        // delete the preview action
        thresholdIds.forEach(thresholdId => {
          // delete the preview thresholds
          dispatch(removeThreshold({
            id: thresholdId,
          }));

          const newThresholdId = generatePublicId('threshold');
          // create a threshold linking the node that initiated linked child action to the outcome
          dispatch(createThreshold({
            thresholdOwnerActionId: outcomeActionBeingLinked.publicId,
            path: `${newThresholdId}.left.actionId`,
            value: {
              leftActionId: ownerNodeId,
            },
          }));
        });

        dispatch(removeAction({
          id: previewActionBeingBuilt.publicId,
        }));

        // reset the canvas
        dispatch(resetNodeAndConnectorState());
        // reset linked child action state
        dispatch(resetLinkedChildActionState());
        // focus on new linked outcome action
        dispatch(setFocusedNodeId(outcomeActionBeingLinked.publicId));
        return;
      }
    }
    if (isActionPayloadEphemeral(createActionPayload)) {
      switch (createActionPayload.type) {
        case ActionType.TAG:
          dispatch(addEphemeralTagToProps({
            name: createActionPayload.args.name,
          }));
          break;
        case ActionType.OUTCOME:
          dispatch(addEphemeralOutcomeToProps({
            name: createActionPayload.args.name,
          }));
          break;
        default:
          // only adding tags + outcomes are supported via the threshold editor
          break;
      }
    }

    // create a new action
    dispatch(createActions(createActionPayload));

    // map the preview action thresholds to the new action
    thresholdIds.forEach(thresholdId => {
      dispatch(updateThreshold({
        path: `${thresholdId}.actionId`,
        value: createActionPayload.args.publicId,
      }));
    });

    // set a rule for the preview action threshold in the new action
    dispatch(updateAction({
      path: `${createActionPayload.args.publicId}.rules`,
      value: previewActionBeingBuilt.rules,
    }));

    /**
     * remove the rules from the preview action so that deleting the action
     * does not delete the threshold
     */
    dispatch(updateAction({
      path: `${previewActionBeingBuilt.publicId}.rules`,
      value: {},
    }));

    // safely remove the preview action completely
    dispatch(removeAction({
      id: previewActionBeingBuilt.publicId,
    }));

    // reset the canvas
    dispatch(resetNodeAndConnectorState());
    // reset linked child action state
    dispatch(resetLinkedChildActionState());
    // focus on new action node
    dispatch(setFocusedNodeId(createActionPayload.args.publicId));
  };
}

export function exitLinkedChildActionThunk(): (
  (dispatch: AppDispatch, getState: () => State) => void
);
export function exitLinkedChildActionThunk(previewActionPublicId: string): (
  (dispatch: AppDispatch) => void
);
export function exitLinkedChildActionThunk(options: {
  preserveActionPublicId?: Action['publicId'];
  cancelUntrackedOutcomeOrRoutingPreview?: boolean;
}): (
  (dispatch: AppDispatch, getState: () => State) => void
);

/**
 * Exits a linked child action. Navigation callers can also cancel an active
 * precedence-indexed preview through the options form.
 */
export function exitLinkedChildActionThunk(
  previewActionPublicIdOrOptions?: string | {
    preserveActionPublicId?: Action['publicId'];
    cancelUntrackedOutcomeOrRoutingPreview?: boolean;
  },
) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const options = typeof previewActionPublicIdOrOptions === 'object'
      ? previewActionPublicIdOrOptions
      : undefined;
    const linkedChildPreviewPublicId = typeof previewActionPublicIdOrOptions === 'string'
      ? previewActionPublicIdOrOptions
      : selectPreviewAction(getState())?.publicId;

    if (
      linkedChildPreviewPublicId
      && linkedChildPreviewPublicId !== options?.preserveActionPublicId
    ) {
      dispatch(removeAction({ id: linkedChildPreviewPublicId }));
      dispatch(resetLinkedChildActionState());
      dispatch(resetNodeAndConnectorState());
    }

    if (!options) return;

    const state = getState();
    const precedenceIndexedPreview = selectPreviewPrecedenceIndexedAction(state);

    if (
      !precedenceIndexedPreview
      || precedenceIndexedPreview.publicId === options.preserveActionPublicId
    ) {
      return;
    }

    const { pendingBeforeState } = state.workflowEditor.editHistory;
    const isPendingPrecedenceIndexedPreview = Boolean(
      pendingBeforeState
      && pendingBeforeState.workflowData.actions?.[
        precedenceIndexedPreview.publicId
      ] === undefined,
    );

    if (
      isPendingPrecedenceIndexedPreview
      || options.cancelUntrackedOutcomeOrRoutingPreview
    ) {
      dispatch(discardWorkflowHistoryTransaction());
      dispatch(removeAction({ id: precedenceIndexedPreview.publicId }));
    }
  };
}

/**
 * Cancels an in-progress multi-step action preview without recording it in history.
 * The pending snapshot is discarded before the known draft is removed so the
 * removal cannot be folded into the Add transaction.
 */
export function cancelMultiStepActionPreviewThunk(
  previewActionPublicId: Action['publicId'],
) {
  return (dispatch: AppDispatch) => {
    dispatch(discardWorkflowHistoryTransaction());
    dispatch(removeAction({ id: previewActionPublicId }));
  };
}

export function duplicateNodeThunk(actionId: Action['publicId']) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();

    // Step 1: Extract all impacted element ids
    const action = selectActionByPublicId(actionId)(state);

    const {
      actionoption: actionOptionIds,
      group: groupIds,
      threshold: thresholdIds,
    } = extractIds(action);

    // Step 2: Duplicate all impacted elements & generate id maps from old -> new ids
    const actionIdMap = {
      [action.publicId]: generatePublicId(elementTypes.action, action.type),
    };

    const actionOptionIdMap = generateRandomizedIdPublicIdMap(Array.from(actionOptionIds));
    const groupIdMap = generateRandomizedIdPublicIdMap(Array.from(groupIds));

    const thresholds = selectThresholdsByIds(Array.from(thresholdIds))(state);
    const { idMap: thresholdIdMap, elements: dupedThresholds } = duplicateElements(thresholds);

    const dataRoutes = selectDataRoutesByTargetId(actionId)(state);
    const { idMap: dataRouteIdMap, elements: dupedDataRoutes } = duplicateElements(dataRoutes);

    const idMap = {
      ...actionIdMap,
      ...actionOptionIdMap,
      ...dataRouteIdMap,
      ...groupIdMap,
      ...thresholdIdMap,
    };

    // Step 3: Construct the new action with remapped ids & add to state
    // remap target action id in duplicated data routes
    const remappedDupedDataRoutes = remapIds({ data: dupedDataRoutes, idMap: actionIdMap });
    // remap owning action ids in duplicated thresholds
    const remappedDupedThresholds = remapIds({ data: dupedThresholds, idMap: actionIdMap });

    const remappedDupedAction = remapIds({ data: action, idMap });

    if (remappedDupedAction.type === ActionType.MATRIX_MODEL && remappedDupedAction.matrixModel) {
      remappedDupedAction.matrixModel = {
        ...remappedDupedAction.matrixModel,
        matrixModelToken: generateTemporaryMatrixModelToken(),
        matrixModelId: null,
        activeMatrixModelVersionId: null,
        matrixModelPublicVersion: null,
      };
    }

    dispatch(addAction({
      action: remappedDupedAction,
      dataRoutes: remappedDupedDataRoutes,
      thresholds: remappedDupedThresholds,
    }));

    dispatch(updateDependencies());
    dispatch(setFocusedNodeId(remappedDupedAction.publicId));
  };
}

/**
 * Handles swapping action data objects (currently supports tags/outcomes)
 * example: Action w/Tag1 => Action w/Tag2
 * Updates action and adds/removes any ephemeral props affected by swap
 */
export function swapActionObjectThunk(replacementName: string) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    // action affected by swap
    const targetAction = selectFocusedAction(state);
    if (!targetAction) return;
    switch (targetAction.type) {
      case ActionType.TAG: {
        const { tags } = selectWorkflowProps(state);
        // tag prop that is being swapped OUT - may need to be removed from props
        const sourceTagProp = getTagByName(tags, targetAction.tag.name);
        // tag prop being swapped IN - may need to be added to props
        const matchedTagProp = getTagByName(tags, replacementName);
        // if no prop is found - add ephemeral to props
        if (!matchedTagProp) {
          dispatch(addEphemeralTagToProps({ name: replacementName }));
        }
        // instead of reselecting to refresh the state + get the new prop, we're generating the same data to use in the action.
        const replacementTagProp = matchedTagProp ?? createEphemeralTag({ name: replacementName });
        const replacementTagObject = getFormattedTagData(replacementTagProp);
        dispatch(updateAction({
          path: `${targetAction.publicId}.tag`,
          value: replacementTagObject,
        }));
        // cleanup any ephemeral props no longer in use
        if (isTagEphemeral(sourceTagProp)) {
          dispatch(removeEphemeralProp({ name: sourceTagProp.tag_name, type: ActionType.TAG }));
        }
        break;
      }
      case ActionType.OUTCOME: {
        const { outcomes } = selectWorkflowProps(state);

        const sourceOutcomeProp = getOutcomeByName(outcomes, targetAction.outcome.name);
        const matchedOutcomeProp = getOutcomeByName(outcomes, replacementName);

        if (!matchedOutcomeProp) {
          dispatch(addEphemeralOutcomeToProps({ name: replacementName }));
        }
        // instead of reselecting to refresh the state + get the new prop, we're generating the same data to use in the action.
        const matchingOutcomeProp = matchedOutcomeProp
          ?? createEphemeralOutcome({ name: replacementName });

        const replacementOutcomeObject: OutcomeObjectInAction = {
          ...targetAction.outcome, // keeps all action-specific settings from original outcome
          name: matchingOutcomeProp.outcome, // not nullable
          color: matchingOutcomeProp.outcome_color ?? null,
          icon: matchingOutcomeProp.icon_name ?? null,
          isCaseStatus: matchingOutcomeProp.is_case_status ?? null,
          isEphemeral: isOutcomeEphemeral(matchingOutcomeProp),
        };
        dispatch(updateAction({
          path: `${targetAction.publicId}.outcome`,
          value: replacementOutcomeObject,
        }));
        if (isOutcomeEphemeral(sourceOutcomeProp)) {
          dispatch(removeEphemeralProp({
            name: sourceOutcomeProp.outcome,
            type: ActionType.OUTCOME,
          }));
        }
        break;
      }
      default:
    }
  };
}
/**
 * Sets an existing precedence indexed Action as the default.
 */
export function setExistingPrecedenceIndexedActionAsDefaultThunk(
  actionToSetAsDefault: Action,
) {
  return (dispatch: AppDispatch, getState: () => State) => {
    validatePrecedenceIndexedAction(actionToSetAsDefault);

    const state = getState();
    const isOutcomeActionType = isActionOfType(
      actionToSetAsDefault, ActionType.OUTCOME,
    );

    const defaultPrecedenceIndexedAction = isOutcomeActionType
      ? selectDefaultOutcomeAction(state)
      : selectDefaultWorkflowRoutingAction(state);

    const thresholdIds = extractThresholdIds(actionToSetAsDefault.rules);


    // delete the thresholds for the action before deleting the action itself
    thresholdIds.forEach(thresholdId => {
      dispatch(removeThreshold({
        id: thresholdId,
      }));
    });

    // delete the existing default PrecedenceIndexed action
    if (defaultPrecedenceIndexedAction) {
      dispatch(removeAction({
        id: defaultPrecedenceIndexedAction.publicId,
      }));
    }

    // default PrecedenceIndexed action have no rules and a specific precedenceIndex
    dispatch(updateAction({
      path: `${actionToSetAsDefault.publicId}.rules`,
      value: {},
    }));


    const precedenceIndexPath = isOutcomeActionType
      ? `${actionToSetAsDefault.publicId}.outcome.precedenceIndex`
      : `${actionToSetAsDefault.publicId}.workflow.precedenceIndex`;

    const precedenceIndexValue = isOutcomeActionType
      ? DEFAULT_OUTCOME_PRECEDENCE_INDEX
      : DEFAULT_WORKFLOW_ROUTING_PRECEDENCE_INDEX;

    dispatch(updateAction({
      path: precedenceIndexPath,
      value: precedenceIndexValue,
    }));
  };
}


/**
 * Increases the precedence of an action if allowed
 */
export function increaseActionPrecedenceThunk(originalAction: Action) {
  return (dispatch: AppDispatch, getState: () => State) => {
    validatePrecedenceIndexedAction(originalAction);

    // manual type narrowing so the rest of the function knows the correct type of action
    if (originalAction.type !== ActionType.OUTCOME && originalAction.type !== ActionType.WORKFLOW) {
      return;
    }

    const state = getState();
    const allActionsOfType = selectPrecedenceIndexedActions(state, originalAction.type);

    /**
     * the bottom (default) and top-most precedence-indexed Actions cannot increase their
     * precedence, hence there need to be at least 3 outcomes to perform this operation
     */
    if (allActionsOfType.length < 3) {
      return;
    }

    const precedenceIndexedActionToSwapPrecedenceWith = findNextActionBasedOnPrecedence(
      originalAction,
      allActionsOfType,
    );

    if (!precedenceIndexedActionToSwapPrecedenceWith) {
      return;
    }

    dispatch(updateAction({
      path: `${originalAction.publicId}.${originalAction.type}.precedenceIndex`,
      value: precedenceIndexedActionToSwapPrecedenceWith[originalAction.type]
        .precedenceIndex,
    }));

    dispatch(updateAction({
      path: `${precedenceIndexedActionToSwapPrecedenceWith.publicId}.${originalAction.type}.precedenceIndex`,
      value: originalAction[originalAction.type].precedenceIndex,
    }));
  };
}

/**
 * Creates the default outcome in the workflow.
 * The outcome of "Manual Review" is hardcoded as the default for now
 */
export function createDefaultOutcomeActionThunk(outcomeName = 'Manual Review', publicId?: string) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const workflowProps = selectWorkflowProps(state);
    const defaultOutcomeAction = selectDefaultOutcomeAction(state);

    // skip if there's already an outcome in the workflow
    if (!workflowProps || defaultOutcomeAction) {
      return;
    }

    const actionPublicId = publicId ?? generatePublicId('action', ActionType.OUTCOME);
    const outcomeToAdd = getOutcomeByName(workflowProps.outcomes, outcomeName);

    dispatch(createActions({
      type: ActionType.OUTCOME,
      args: {
        publicId: actionPublicId,
        color: outcomeToAdd.outcome_color,
        icon: outcomeToAdd.icon_name,
        isCaseStatus: outcomeToAdd.is_case_status,
        name: outcomeToAdd.outcome,
        precedenceIndex: 1,
        isEphemeral: outcomeToAdd.is_ephemeral ?? false,
        rules: {},
      },
    }));
    dispatch(setFocusedNodeId(actionPublicId));
  };
}

/**
 * Replaces an outcome for another
 * @param nextOutcomeName name of outcome to add (lookup in props)
 * @param publicId optional publicId to use in the created Action
 */
export function replaceDefaultOutcomeThunk(nextOutcomeName: string, publicId?: string) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const defaultOutcomeAction = selectDefaultOutcomeAction(state);
    dispatch(removeAction({
      id: defaultOutcomeAction.publicId,
    }));

    dispatch(createDefaultOutcomeActionThunk(nextOutcomeName, publicId));
  };
}

export function replaceDefaultPrecedenceIndexedAction(
  action: Action, opts?: { publicId: string },
) {
  const { publicId } = opts ?? {};
  return (dispatch: AppDispatch) => {
    if (isActionOfType(action, ActionType.OUTCOME)) {
      dispatch(replaceDefaultOutcomeThunk(getActionName(action), publicId));
    }

    if (isActionOfType(action, ActionType.WORKFLOW)) {
      dispatch(replaceDefaultWorkflowRoutingThunk(action.workflow.workflowId, publicId));
    }
  };
}


/**
 * Creates the default workflow routing action in the workflow.
 *
 * if no `workflowId` is passed, the first workflow from the list (order is not deterministic)
 * will be set as the default workflow
 *
 * optionally pass a `publicId` to use in the created Action
 *
 */
export function createDefaultWorkflowRoutingActionThunk(workflowId?: number, publicId?: string) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const workflowProps = selectWorkflowProps(state);
    const defaultWorkflowRoutingAction = selectDefaultWorkflowRoutingAction(state);

    // skip if there's already workflow routing in the workflow
    if (!workflowProps || defaultWorkflowRoutingAction) {
      return;
    }

    const actionPublicId = publicId ?? generatePublicId('action', ActionType.WORKFLOW);
    const workflowToAdd = workflowId
      ? (workflowProps
        .allWorkflows
        .find(w => w.application_id === workflowId))
      // when adding the first workflow, we grab the first one from the list as a sane default
      : (state.workflowEditor.workflowProps.allWorkflows[0]);


    dispatch(createActions({
      type: ActionType.WORKFLOW,
      args: {
        publicId: actionPublicId,
        name: workflowToAdd.name,
        precedenceIndex: DEFAULT_WORKFLOW_ROUTING_PRECEDENCE_INDEX,
        workflowId: workflowToAdd.application_id,
        // we might have to add this later
        // rules: {},
      },
    }));
    dispatch(setFocusedNodeId(actionPublicId));
  };
}


/**
 * Sets an existing workflow routing action as the default.
 * optionally pass a `publicId` to use in the created Action
 */
export function replaceDefaultWorkflowRoutingThunk(nextWorkflowId: number, publicId?: string) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const defaultWorkflowRoutingAction = selectDefaultWorkflowRoutingAction(state);


    dispatch(removeAction({
      id: defaultWorkflowRoutingAction.publicId,
    }));

    dispatch(createDefaultWorkflowRoutingActionThunk(nextWorkflowId, publicId));
  };
}


export function createActionsThunk(payload: CreateActionPayload | CreateActionPayload[]) {
  return (dispatch: AppDispatch) => {
    // continue as normal if creating multiple Actions in a single request
    if (Array.isArray(payload) && payload.length > 1) {
      dispatch(createActions(payload));
      return;
    }

    /**
     * The editor should focus on the newly created node. To do that, we explicitly assign
     * a publicId (when one is not available) and set it as focused after the action is created.
     * This should only happen when there's a single Action being created.
     */
    let payloadItem = Array.isArray(payload) ? payload[0] : payload;
    if (!payloadItem.args.publicId) {
      /**
       * the payload comes in from a component so we don't want to mutate it as it might be
       * used as state within it.
       */
      payloadItem = structuredClone(payloadItem);
      payloadItem.args.publicId = generatePublicId('action', payloadItem.type);
    }

    dispatch(createActions(payloadItem));
    dispatch(setFocusedNodeId(payloadItem.args.publicId));
  };
}

/**
 * Thunk to initialize the testing suite from the new editor.
 * Note: the logic here is slightly different from the legacy editor.
 */
export function initializeTestingSuiteThunk() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();

    const initialWorkflowProps = selectInitialWorkflowProps(state);
    const initialWorkflowData = selectInitialWorkflowData(state);
    const workflowData = selectWorkflowData(state);
    const workflowProps = selectWorkflowProps(state);
    const featureFlagsForStore = selectFeatureFlagsForStore(state);

    const {
      token: workflowToken,
      publicVersion,
    } = workflowData;

    const changesForTestingSuite = generateVersionChangelog({
      A: {
        props: initialWorkflowProps,
        version: initialWorkflowData,
      },
      B: {
        props: workflowProps,
        version: workflowData,
      },
      formatter: formatters.testingSuiteFormatter,
      featureFlags: featureFlagsForStore,
    });

    dispatch(testingSuiteActions.setWorkflowToken(workflowToken));
    dispatch(testingSuiteActions.setVersionToTest(publicVersion));
    dispatch(testingSuiteActions.setIsTestingSuiteVisible(true));

    const hasUnsavedTestingSuiteChanges = Object
      .values(changesForTestingSuite.content.data).length;
    /**
     * the default first page in the testing suite is the PreviewChanges page. we manually
     * skip it if there are no changes to show
     */
    if (!hasUnsavedTestingSuiteChanges) {
      dispatch(testingSuiteActions.setCurrentPage('selectDataType'));
    }

    if (publicVersion !== null) {
      dispatch(testingSuiteActions.setVersionToTestAgainst(publicVersion));
    }
  };
}

// ================ Node Container Thunks  ================
export function toggleNodeFolderCollapsibleThunk() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { isCollapsed } = selectNodeFolderState(state);
    const focusedAction = selectFocusedAction(state);

    if (isActionOfType(focusedAction, ActionType.INPUT)) {
      dispatch(resetNodeAndConnectorState());
    }
    dispatch(setNodeFolderCollapsed({ isCollapsed: !isCollapsed }));
  };
}

export function toggleSnippetCollapsibleThunk(snippetId: PublicId) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { isCollapsed } = selectSnippetCollapsedState(snippetId)(state);

    // todo: cleanup focused action state if collapsing and child node is focused

    dispatch(setSnippetCollapsed({ isCollapsed: !isCollapsed, snippetId }));
  };
}


// ================ Search State Thunks ================
export function updateSearchMatchIndexThunk(payload: {
  direction: 'next' | 'prev';
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const matchIndex = selectSearchMatchIndex(state);
    const matchedNodes = selectNodesMatchingSearch(state);

    const matchesCount = matchedNodes.length;

    // safely compute the next index based on the direction - wraps for overflow and underflow
    const nextIndex = payload.direction === 'next'
      // add until larger than matchesCount, then back down to 0. -- ie: 0, 1, 2, 3 -> 0, ..
      ? (matchIndex + 1) % matchesCount
      // subtract until less than 0, then back up to (matchesCount - 1). -- ie: 3, 2, 1, 0 -> 3 ..
      : (matchIndex - 1 + matchesCount) % matchesCount;

    dispatch(setSearchMatchIndex(nextIndex));
  };
}

export function setSearchQueryThunk(payload: {
  searchQuery: string;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    dispatch(setSearchQuery(payload.searchQuery));

    const state = getState();
    const hasCollapsedInputMatches = selectCollapsedInputHasMatches(state);
    const collapsedSnippetIdsWithMatches = selectCollapsedSnippetIdsWithMatches(state);

    if (hasCollapsedInputMatches) {
      dispatch(toggleNodeFolderCollapsibleThunk());
    }
    collapsedSnippetIdsWithMatches.forEach(snippetId => {
      dispatch(toggleSnippetCollapsibleThunk(snippetId));
    });
  };
}

export function closeSearchBoxThunk() {
  return (dispatch: AppDispatch) => {
    dispatch(setIsSearchBoxVisible(false));
    dispatch(setSearchQuery(''));
    dispatch(setSearchMatchIndex(0));
  };
}

export function toggleShouldRenderCriticalNodePath() {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const shouldRenderCriticalNodePath = selectShouldRenderCriticalNodePath(state);

    dispatch(setShouldRenderCriticalNodePath(!shouldRenderCriticalNodePath));
  };
}
